import { lazy, useEffect } from "react";
import { useParams, useNavigate, useLocation, Outlet } from "react-router-dom";
import i18n from "../i18n";

const NotFound = lazy(() => import("../contents/pages/NotFound"));

const SUPPORTED_LANGS = ["fr", "zh", "zh-TW", "pa", "es", "tl", "ar", "ko", "ja"];

function stripLangPrefix(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGS.includes(segments[0])) {
    return "/" + segments.slice(1).join("/");
  }
  return pathname;
}

export default function LangRouter() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Handle ?lang= redirect for backward compatibility
    const params = new URLSearchParams(location.search);
    const queryLang = params.get("lang");
    if (queryLang && SUPPORTED_LANGS.includes(queryLang)) {
      const basePath = stripLangPrefix(location.pathname);
      navigate(`/${queryLang}${basePath}`, { replace: true });
      return;
    }
    if (queryLang === "en") {
      const basePath = stripLangPrefix(location.pathname);
      navigate(basePath || "/", { replace: true });
      return;
    }

    // Set language from URL path
    if (lang && SUPPORTED_LANGS.includes(lang)) {
      if (i18n.language !== lang) i18n.changeLanguage(lang);
    } else {
      if (i18n.language !== "en") i18n.changeLanguage("en");
    }
  }, [lang, location, navigate]);

  // Guard against the `:lang` route segment matching arbitrary first-path
  // segments. Without this, hitting e.g. `/totally-bogus-xyz` would slip
  // through with `lang=totally-bogus-xyz`, render <Outlet /> and the
  // index route would happily serve the homepage — silently swallowing
  // typos as 200/home instead of surfacing a real 404.
  if (lang && !SUPPORTED_LANGS.includes(lang)) {
    return <NotFound />;
  }

  return <Outlet />;
}
